/* global google */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  GoogleMap,
  Polyline,
  withGoogleMap,
  withScriptjs,
  Polygon,
} from 'react-google-maps'
import { Marker } from './Marker'
import { PathPoint } from './PathPoint'

import { CONFIG } from 'Config/config'
import currentPositionIcon from '../../Assets/Images/icons/currentLocation.svg'

const centerCoordinates = (useDefaultCenter, lat, lng) => ({
  [useDefaultCenter ? 'defaultCenter' : 'center']: {
    lat,
    lng,
  },
})

export const Map = props => {
  const {
    children,
    defaultZoom,
    draggable,
    isMarkerDraggable,
    isMarkerShown,
    mapPositionLat,
    mapPositionLng,
    onMarkerDragEnd,
    markers,
    path,
    pathColor,
    onMapLoad,
    useDefaultCenter,
    mapElementStyle,
    containerElementStyle,
    onZoomChanged,
    shouldShowCurrentPosition,
    polygon,
  } = props

  const [currentLocation, setCurrentLocation] = useState(null)

  useEffect(() => {
    if (shouldShowCurrentPosition) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          savePosition,
          getCurrentPositionError,
          {
            enableHighAccuracy: true,
            maximumAge: 60000,
            timeout: 4000,
          },
        )
      } else {
        getCurrentPositionError()
      }
    }
  }, [])

  const savePosition = ({ coords }) => {
    setCurrentLocation({
      lat: coords.latitude,
      lng: coords.longitude,
    })
  }

  const getCurrentPositionError = err => {
    console.warn('MissionMap > Map > getCurrentPositionError', { err })
    setCurrentLocation(null)
  }

  return (
    <GoogleMapService
      onMapLoad={onMapLoad}
      useDefaultCenter={useDefaultCenter}
      containerElement={
        <div style={{ height: '400px', ...containerElementStyle }} />
      }
      children={children}
      defaultZoom={defaultZoom}
      googleMapURL={CONFIG.GOOGLE_API_URL}
      isMarkerDraggable={isMarkerDraggable || false}
      isMarkerShown={isMarkerShown}
      mapPositionLat={mapPositionLat}
      mapPositionLng={mapPositionLng}
      loadingElement={<div style={{ height: '100%' }} />}
      mapElement={<div style={{ height: '100%', ...mapElementStyle }} />}
      onMarkerDragEnd={onMarkerDragEnd || null}
      markers={markers}
      path={path}
      pathColor={pathColor}
      draggable={draggable}
      onZoomChanged={onZoomChanged}
      polygon={polygon}
    >
      {shouldShowCurrentPosition && currentLocation ? (
        <Marker
          key={'currentLocationMarker'}
          position={currentLocation}
          title={'Current position'}
          icon={{
            url: currentPositionIcon,
            scaledSize: {
              width: 20,
              height: 20,
            },
          }}
        />
      ) : null}
    </GoogleMapService>
  )
}

Map.propTypes = {
  defaultZoom: PropTypes.number.isRequired,
  draggable: PropTypes.bool,
  mapPositionLat: PropTypes.number,
  mapPositionLng: PropTypes.number,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      markerPositionLat: PropTypes.number.isRequired,
      markerPositionLng: PropTypes.number.isRequired,
      onMarkerDragEnd: PropTypes.func,
    }),
  ),
  onMapLoad: PropTypes.func,
  useDefaultCenter: PropTypes.bool.isRequired,
  onZoomChanged: PropTypes.func,
}

Map.defaultProps = {
  useDefaultCenter: true,
  markers: [],
}

const GoogleMapService = withScriptjs(
  withGoogleMap(props => {
    var {
      children,
      defaultZoom,
      draggable,
      mapPositionLat,
      mapPositionLng,
      markers,
      path,
      pathColor,
      onMapLoad,
      useDefaultCenter,
      onZoomChanged,
      polygon,
    } = props
    path = (path || []).filter(p => p)

    const lat = mapPositionLat
    const lng = mapPositionLng

    const centerProps =
      lat && lng
        ? centerCoordinates(useDefaultCenter, lat, lng)
        : {
            defaultCenter: {
              lat: 0,
              lng: 0,
            },
          }

    const icon = (index, pathLength, activity) => {
      if (activity === 'checkout' || activity === 'checkin') {
        return 'none'
      }
      if (index === pathLength - 1) {
        return {
          path:
            'M16.958 11.198c0-6.181 5.01-11.19 11.191-11.19 6.181 0 11.191 5.01 11.191 11.19s-5.011 11.19-11.191 11.19c-6.181 0-11.191-5.01-11.191-11.19zM73.448 18H63v7.275h10.448V18zm-5.499 8.644c1.578 1.996 1.043 4.974-.848 6.346L52.313 44.013a4.23 4.23 0 0 1-5.907-.938l-7.178-8.763v60.657c0 2.777-2.272 5.031-5.075 5.031-2.803 0-5.076-2.254-5.076-5.031V65.68h-1.856v29.289c0 2.777-2.272 5.031-5.075 5.031-2.803 0-5.075-2.254-5.075-5.031V61.178a4.075 4.075 0 0 1-2.074 1.848 4.086 4.086 0 0 1-5.31-2.275L1.099 44.076a4.089 4.089 0 0 1 .402-3.795l8.667-10.806c3.272-4.603 6.94-5.317 9.522-5.317h16.919c2.582 0 6.249.715 9.521 5.317l4.315 5.381 11.687-8.712c.06-.043.791-.101.868-.144h2c1.79 0 2.275-.208 2.949.644zm-50.878 7.668l-7.159 8.733 7.159 14.269V34.312z',
          fillColor: 'black',
          fillOpacity: 1,
          fillRule: 'evenodd',
          scale: 0.3,
          strokeColor: 'black',
          strokeWeight: 1,
          anchor: new google.maps.Point(20, 100),
        }
      }
    }

    return (
      <GoogleMap
        ref={map =>
          onMapLoad && onMapLoad(window.google, { path, markers, map })
        }
        defaultZoom={defaultZoom}
        {...centerProps}
        defaultOptions={{
          draggable,
          streetViewControl: false,
          scaleControl: false,
          mapTypeControl: false,
          panControl: false,
          zoomControl: false,
          rotateControl: false,
          fullscreenControl: false,
        }}
        onZoomChanged={onZoomChanged}
      >
        {Array.isArray(polygon)
          ? polygon.map((poly, idx) => <Polygon key={idx} {...poly} />)
          : polygon && <Polygon {...polygon} />}
        {path && (
          <Polyline
            path={path}
            options={{ strokeColor: pathColor, strokeOpacity: 0.9 }}
          />
        )}
        {path.map((point, i) => (
          <PathPoint
            key={`polyline-${i}`}
            color={pathColor}
            point={point}
            icon={icon(i, path.length, point.activity)}
          />
        ))}
        {markers.map((marker, i) => (
          <Marker
            key={i}
            {...marker}
            draggable={marker.isMarkerDraggable}
            onDragEnd={marker.onMarkerDragEnd}
            position={{
              lat: marker.markerPositionLat,
              lng: marker.markerPositionLng,
            }}
            icon={
              marker.icon || {
                path: google.maps.SymbolPath.CIRCLE,
                scale: marker.size,
                strokeColor: marker.color,
              }
            }
          />
        ))}
        {children}
      </GoogleMap>
    )
  }),
)
